import { createContext } from 'react';

import type { usePublicationPreferences } from '~/routes/subscribe/$subscriberId/_components/v2/hooks/usePublicationPreferences';

type Preferences = ReturnType<typeof usePublicationPreferences>['preferences'];

/**
 * Logic layer for the subscription **preferences** surface on the General tab — the
 * `PreferencesElement` (publication subscriptions + custom preference fields). Self-calls
 * `usePublicationPreferences` (the org-subscriptions + subscriber-preferences composite) and
 * `usePreferenceActions` (the six mutation callbacks), then exposes the resolved data + handlers
 * via context. Holds no UI; the colocated `PreferencesSection` child renders from it. Account /
 * profile, subscription lifecycle, and billing are separate domains and stay out.
 */
export type PreferencesContextType = {
  showPreferences: boolean;
  preferences: Preferences;
  email: string;

  onSubscriptionPreferenceChange: (enabled: boolean, publicationId: string) => void;
  onTrueFalsePreferenceChange: (value: boolean, preferenceId: string) => void;
  onShortResponsePreferenceChange: (value: string, preferenceId: string) => void;
  onDropdownPreferenceChange: (value: string, preferenceId: string) => void;
  onMultipleChoicePreferenceChange: (values: string[], preferenceId: string) => void;
  onSingleChoicePreferenceChange: (value: string, preferenceId: string) => void;
};

export const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);
PreferencesContext.displayName = 'PreferencesContext';
